import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, User, Tag, ChevronRight, ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Article Schema
const ArticleSchema = ({ blog }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": blog.title,
    "description": blog.excerpt,
    "author": {
      "@type": "Person",
      "name": blog.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "Plutops",
      "url": window.location.origin
    },
    "datePublished": blog.published_at,
    "image": blog.image_url
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

// FAQ Schema
const FAQSchema = ({ faq }) => {
  if (!faq || faq.length === 0) return null;
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faq.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

const BlogDetailPage = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const response = await axios.get(`${API}/blog/${slug}`);
        setBlog(response.data);
        document.title = `${response.data.title} | Plutops Blog`;
      } catch (error) {
        console.error('Failed to fetch blog:', error);
        setError('Blog post not found');
      } finally {
        setLoading(false);
      }
    };
    fetchBlog();
  }, [slug]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Generate TOC from content
  const generateTOC = (content) => {
    const headingRegex = /^##\s+(.+)$/gm;
    const matches = [...content.matchAll(headingRegex)];
    return matches.map((match, i) => ({
      id: `section-${i}`,
      title: match[1]
    }));
  };

  // Render markdown-like content
  const renderContent = (content) => {
    const lines = content.split('\n');
    let inCodeBlock = false;
    let codeContent = [];
    const elements = [];
    let key = 0;

    lines.forEach((line, index) => {
      if (line.startsWith('```')) {
        if (inCodeBlock) {
          elements.push(
            <pre key={key++} className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto my-4 font-mono text-sm">
              <code>{codeContent.join('\n')}</code>
            </pre>
          );
          codeContent = [];
        }
        inCodeBlock = !inCodeBlock;
        return;
      }

      if (inCodeBlock) {
        codeContent.push(line);
        return;
      }

      if (line.startsWith('# ')) {
        elements.push(
          <h1 key={key++} className="font-heading font-bold text-3xl text-text-primary mt-8 mb-4">
            {line.substring(2)}
          </h1>
        );
      } else if (line.startsWith('## ')) {
        elements.push(
          <h2 key={key++} id={`section-${key}`} className="font-heading font-bold text-2xl text-text-primary mt-8 mb-4">
            {line.substring(3)}
          </h2>
        );
      } else if (line.startsWith('### ')) {
        elements.push(
          <h3 key={key++} className="font-heading font-bold text-xl text-text-primary mt-6 mb-3">
            {line.substring(4)}
          </h3>
        );
      } else if (line.startsWith('- ') || line.startsWith('* ')) {
        elements.push(
          <li key={key++} className="ml-4 text-text-secondary">
            {line.substring(2)}
          </li>
        );
      } else if (line.startsWith('| ')) {
        // Skip table rows - handled separately
      } else if (line.trim()) {
        // Handle bold and italic
        let processed = line
          .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
          .replace(/\*(.+?)\*/g, '<em>$1</em>');
        elements.push(
          <p 
            key={key++} 
            className="text-text-secondary mb-4 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: processed }}
          />
        );
      }
    });

    return elements;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h1 className="text-2xl font-heading font-bold text-text-primary mb-4">Article Not Found</h1>
        <Link to="/blog">
          <Button className="bg-primary text-white rounded-full">
            View All Articles
          </Button>
        </Link>
      </div>
    );
  }

  const toc = generateTOC(blog.content);

  return (
    <div data-testid="blog-detail-page">
      {/* Schema Markup */}
      <ArticleSchema blog={blog} />
      <FAQSchema faq={blog.faq} />

      {/* Breadcrumb */}
      <div className="bg-warm-50 py-4">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center text-sm text-text-muted">
            <Link to="/" className="hover:text-primary">Home</Link>
            <ChevronRight className="h-4 w-4 mx-2" />
            <Link to="/blog" className="hover:text-primary">Blog</Link>
            <ChevronRight className="h-4 w-4 mx-2" />
            <span className="text-text-primary line-clamp-1">{blog.title}</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-gradient-to-br from-warm-50 to-white py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Link 
              to="/blog" 
              className="inline-flex items-center text-primary hover:text-primary-hover mb-6 font-medium"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Blog
            </Link>

            <div className="flex flex-wrap gap-2 mb-6">
              {blog.tags.map((tag, i) => (
                <span
                  key={i}
                  className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium"
                >
                  <Tag className="h-3 w-3 mr-1" />
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-text-primary mb-6">
              {blog.title}
            </h1>

            <div className="flex items-center space-x-6 text-text-muted">
              <span className="flex items-center">
                <User className="h-5 w-5 mr-2" />
                {blog.author}
              </span>
              <span className="flex items-center">
                <Calendar className="h-5 w-5 mr-2" />
                {formatDate(blog.published_at)}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Image */}
      {blog.image_url && (
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 -mt-8">
          <motion.img
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            src={blog.image_url}
            alt={blog.title}
            className="w-full h-[400px] object-cover rounded-2xl shadow-xl"
          />
        </div>
      )}

      {/* Content */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-4 gap-12">
            {/* TOC Sidebar */}
            {toc.length > 0 && (
              <aside className="hidden lg:block">
                <div className="sticky top-28">
                  <h3 className="font-heading font-bold text-sm text-text-muted uppercase tracking-wide mb-4">
                    Table of Contents
                  </h3>
                  <nav className="space-y-2">
                    {toc.map((item, i) => (
                      <a
                        key={i}
                        href={`#${item.id}`}
                        className="block text-sm text-text-secondary hover:text-primary transition-colors py-1"
                      >
                        {item.title}
                      </a>
                    ))}
                  </nav>
                </div>
              </aside>
            )}

            {/* Main Content */}
            <article className={`${toc.length > 0 ? 'lg:col-span-3' : 'lg:col-span-4 max-w-4xl mx-auto'}`}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="prose prose-lg max-w-none"
              >
                {renderContent(blog.content)}
              </motion.div>

              {/* FAQ Section */}
              {blog.faq && blog.faq.length > 0 && (
                <div className="mt-16 pt-16 border-t border-slate-200">
                  <h2 className="font-heading font-bold text-2xl text-text-primary mb-8">
                    Frequently Asked Questions
                  </h2>
                  <Accordion type="single" collapsible className="space-y-4">
                    {blog.faq.map((item, index) => (
                      <AccordionItem
                        key={index}
                        value={`faq-${index}`}
                        className="bg-slate-50 rounded-xl border border-slate-100 px-6 overflow-hidden"
                      >
                        <AccordionTrigger className="text-left font-heading font-medium text-text-primary hover:text-primary py-4">
                          {item.question}
                        </AccordionTrigger>
                        <AccordionContent className="text-text-secondary pb-4">
                          {item.answer}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              )}
            </article>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-white mb-4">
            Ready to Transform Your Business?
          </h2>
          <p className="text-white/80 mb-8">
            Let's discuss how we can help you achieve your digital goals.
          </p>
          <Link to="/contact">
            <Button className="bg-white text-primary hover:bg-warm-100 rounded-full px-8 py-6">
              Get in Touch
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BlogDetailPage;
